import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["centerContent", "children", "contextual", "onClick", "onKeyDown", "tabIndex", "use", "width"];
import styled from 'foundations-theming/utils';
import { Clickable } from '../Clickable';
import { SandboxContext } from '../../utils/SandboxContext';
import { useContext, forwardRef } from 'react';
import { rgba } from '../../utils/color';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { deepMerge } from '../../utils/deepMerge/deepMerge';
import { useModalPanelContext } from '../ModalPanel/ModalPanelProvider';
import { BREAKPOINTS } from '../Grid/Grid.types';
import { JUPITER_LAYER } from '../../constants';
import { jsx as _jsx } from "react/jsx-runtime";
const StyledInner = styled.div.withConfig({
  displayName: "Overlay__StyledInner",
  componentId: "sc-1vd68hq-0"
})(({
  $centerContent,
  $width,
  $cssStyles
}) => Object.assign({
  maxWidth: $centerContent ? $width : '100%',
  margin: '0 auto',
  height: $centerContent ? '100%' : undefined
}, $cssStyles));
export const StyledOverlayElement = styled(Clickable)(({
  $use,
  $contextual,
  theme,
  $styleOverrides
}) => {
  return deepMerge({
    backgroundColor: $use === 'light' ? rgba(theme.color['fill-surface-default'], 0.79) : rgba(theme.color['temp-do-not-use-fill-surface-recessed'], 0.79),
    bottom: 0,
    left: 0,
    position: $contextual ? 'absolute' : 'fixed',
    contain: $contextual ? 'none' : 'content',
    overflow: 'auto',
    right: 0,
    top: 0,
    zIndex: JUPITER_LAYER,
    '&': {
      [`@media only screen and (min-width: ${BREAKPOINTS.sm}px)`]: {
        paddingBlock: `${theme.space['1300']}`
      }
    }
  }, $styleOverrides);
});
export const Overlay = /*#__PURE__*/forwardRef((props, ref) => {
  var _modalPanelContext$ov;
  const {
      centerContent = true,
      children,
      contextual = false,
      onClick,
      onKeyDown,
      tabIndex,
      use = 'dark',
      width
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const handleClick = evt => {
    if (onClick) onClick(evt);
  };
  const handleKeyDown = evt => {
    if (onKeyDown) onKeyDown(evt);
  };
  const sandboxContext = useContext(SandboxContext);
  const isSandboxed = contextual || sandboxContext.sandboxed;
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const modalPanelContext = useModalPanelContext();
  const isClickable = onClick;
  const as = !isClickable ? 'div' : undefined;
  const overlayProps = isClickable ? {
    cursor: 'auto',
    buttonRef: ref
  } : {
    ref
  };
  return /*#__PURE__*/_jsx(StyledOverlayElement, Object.assign({}, domProps, overlayProps, {
    $styleOverrides: (_modalPanelContext$ov = modalPanelContext === null || modalPanelContext === void 0 ? void 0 : modalPanelContext.overlayStylesOverrides) !== null && _modalPanelContext$ov !== void 0 ? _modalPanelContext$ov : {},
    $use: use,
    $contextual: isSandboxed,
    as: as,
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    role: "presentation",
    tabIndex: isSandboxed ? undefined : tabIndex,
    "data-tinymce-floating-element": "true",
    children: /*#__PURE__*/_jsx(StyledInner, {
      $width: width,
      $centerContent: centerContent,
      $cssStyles: cssStyles,
      children: children
    })
  }));
});